import React from 'react';
import Image from '@/components/ui/image';
import { CreditCard } from 'lucide-react';
import { formatCurrency } from '@/lib/portfolioCalculations';
import { formatDistanceToNow } from 'date-fns';
import type { InstitutionGroup } from '@/providers/wealthica2/utils/display-utils';

interface InstitutionHeaderProps {
  institution: InstitutionGroup;
  lastSyncAt?: Date | null;
  institutionOpen: boolean;
  isInstitutionPositionsLoading?: boolean;
}

export default function InstitutionHeader({
  institution,
  lastSyncAt,
  institutionOpen,
  isInstitutionPositionsLoading,
}: InstitutionHeaderProps) {
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center space-x-3">
        {institution.logoUrl ? (
          <div className="w-8 h-8 overflow-hidden flex-shrink-0 rounded-full bg-white p-1 shadow-sm">
            <Image
              src={institution.logoUrl}
              alt={institution.name}
              className="h-full w-full object-contain"
              width={32}
              height={32}
            />
          </div>
        ) : (
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
            <CreditCard className="h-4 w-4" />
          </div>
        )}
        <div className="flex flex-col items-start">
          <h3 className="font-semibold text-lg text-left">
            {institution.name} ({institution.accounts?.length || 0})
          </h3>
          {lastSyncAt && institutionOpen && (
            <div className="text-xs text-muted-foreground mt-0.5">
              Last updated: {formatDistanceToNow(new Date(lastSyncAt), { addSuffix: true })}
            </div>
          )}
        </div>
      </div>
      <div className="font-semibold text-lg">
        {isInstitutionPositionsLoading ? (
          <div className="w-24 h-5 bg-gray-200 animate-pulse rounded" />
        ) : (
          formatCurrency(
            institution.totalBalance,
            institution.accounts[0]?.currency || 'CAD',
            { showDecimals: true, locale: 'en-CA' }
          )
        )}
      </div>
    </div>
  );
}
